import React, { useState, useRef, forwardRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  Popper,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { TwoFactorDto, VerifyTwoFactorDto, IncreaseFailedAttemptsCountDto } from "../../types/User";
import useApi from "../../hooks/useApi";
import {
  generateQrCode,
  verify2FACode,
  increaseFailedAttemptsCount,
} from "../../services/userService";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltSharpIcon from "@mui/icons-material/TaskAltSharp";
import { HelpOutline } from "@mui/icons-material";
import { POPOVERMESSAGES, USERSIGNUPMESSAGES, USERSIGNINMESSAGES } from "../../constants/messages";
import OtpHandler from "./OtpVerificationComponent";
import {
  USERINFO,
  SIGNINRESPONSES,
  SYSTEMSETTINGS,
  GLOBALSETTINGS,
  COMMONCONSTANTS,
  VERIFICATIONTYPE,
} from "../../data/constants";
import { useSettingValue } from "../../services/useSettingValue";
import OtpInput from "../CommonComponent/CustomOTP";

interface SignUpWithTwoFactorAuthProps {
  onParentAction: (source?: string) => Promise<boolean>;
  onVerificationCodeComplete: (verificationCode: string, isVerified: boolean) => void;
  headerText: string;
  email: string;
  phone: string;
  userName: string;
  otpSendTo: string;
  onOtpVerified: (otp: string, isOtpMatched: boolean, secretKey?: string) => void;
  onIsSessionLocked: () => void;
}
export interface SignUpWithTwoFactorAuthRef {
  childComponetCall: () => boolean;
}
const SignUpWithTwoFactorAuth = forwardRef<
  SignUpWithTwoFactorAuthRef,
  SignUpWithTwoFactorAuthProps
>(
  ({
    onParentAction,
    headerText,
    email,
    phone,
    userName,
    otpSendTo,
    onOtpVerified,
    onIsSessionLocked,
  }) => {
    SignUpWithTwoFactorAuth.displayName = "";
    const { loading, executeApi } = useApi();
    const [openOTPDialog, setOpenOTPDialog] = useState(false);
    const [tokenErrorMessage, setTokenErrorMessage] = useState("");
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const [isSessionLocked, setIsSessionLocked] = useState(false);
    const otpHandlerRef = useRef(null);
    const otpRef = useRef(null);
    const [authAppState, setAuthAppState] = useState({
      isSetUp: false,
      isVerified: false,
      isVisible: true,
      qrCode: "",
      secret: "",
      inputToken: "",
    });

    const updateAuthAppState = (newState: Partial<typeof authAppState>) => {
      setAuthAppState((prevState) => ({
        ...prevState,
        ...newState,
      }));
    };
    const otpFieldLength =
      Number(useSettingValue(SYSTEMSETTINGS.OTP_FIELD_LENGTH)) ||
      GLOBALSETTINGS.DEFAULT_OTP_FIELD_LENGTH;
    const handleGenerate2FA = async () => {
      if (!(await onParentAction())) return;
      const emailOrPhone = email?.trim() ? email : phone;
      const data = await executeApi<string, TwoFactorDto>(generateQrCode, emailOrPhone);

      if (data !== null) {
        const qrCodeImg = "data:image/png;base64," + data.qrCodeBase64;
        updateAuthAppState({ qrCode: qrCodeImg, secret: data.secretKey });
        setOpen(true);
        setStep(1);
        clearOtpField();
      }
    };
    const clearOtpField = () => {
      focusFirstTextField();
      otpRef.current?.clear();
      setTokenErrorMessage("");
      updateAuthAppState({ inputToken: "" });
    };

    const verify2FA = async () => {
      if (!validateToken()) return;
      const verifyTwoFactorData: VerifyTwoFactorDto = {
        secretKey: authAppState.secret,
        code: authAppState.inputToken,
        email: "",
      };
      const isVerify =
        (await executeApi<VerifyTwoFactorDto, boolean>(verify2FACode, verifyTwoFactorData)) ??
        false;
      updateAuthAppState({
        isVerified: isVerify,
        isSetUp: isVerify ? false : authAppState.isSetUp,
      });

      setTokenErrorMessage(isVerify ? "" : USERSIGNUPMESSAGES.AUTHENTICATION_CODE_DOES_NOT_MATCH);
      if (isVerify) {
        setLoadingVerify(true);
        setTimeout(() => {
          setLoadingVerify(false);
          onOtpVerified("", true, authAppState.secret);
          setOpen(false);
        }, 1500);
        updateAuthAppState({ isVisible: false });
      } else {
        onIncreaseFailedAttemptsCount();
      }
    };
    const onIncreaseFailedAttemptsCount = async () => {
      const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);
      const empAndOtpData: IncreaseFailedAttemptsCountDto = {
        sessionValue: sessionValue,
      };
      const updateResult = await executeApi<IncreaseFailedAttemptsCountDto, string>(
        increaseFailedAttemptsCount,
        empAndOtpData,
      );
      if (updateResult === SIGNINRESPONSES.SESSION_LOCKED) {
        setIsSessionLocked(true);
        onIsSessionLocked();
      }
    };

    const validateToken = () => {
      if (!authAppState.inputToken) {
        setTokenErrorMessage(USERSIGNUPMESSAGES.AUTHENTICATION_CODE_REQUIRED);
        focusFirstTextField();
        return false;
      }
      if (authAppState.inputToken.length < otpFieldLength) {
        setTokenErrorMessage(USERSIGNUPMESSAGES.AUTHENTICATION_CODE_6_DIGIT_NUMBER);
        return false;
      }
      setTokenErrorMessage("");
      return true;
    };

    const focusFirstTextField = () => {
      if (!otpRef.current?.isComplete()) {
        otpRef.current?.focusFirstEmpty();
        return;
      }
    };

    const uiCondition = false;
    //dialog

    const [loadingVerify, setLoadingVerify] = useState(false);
    const [step, setStep] = useState(1); // Step state (1 = QR Code, 2 = OTP Input)
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = async () => {
      if (!(await onParentAction(VERIFICATIONTYPE.OTP))) return;
      setTimeout(() => {
        setLoadingVerify(false);
        setOpenOTPDialog(true);
        if (otpHandlerRef.current) {
          otpHandlerRef.current.childComponetCall();
          setOpen(false);
        }
      }, 10);
    };
    const handleClose = () => {
      setOpen(false);
    };
    const handleCopy = () => {
      navigator.clipboard.writeText(authAppState.secret).then(() => {
        setShowCopiedMessage(true);
      });
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 2000);
    };
    const onCheckOtpVerify = (otp: string, isVerified: boolean) => {
      if (isVerified) {
        handleGenerate2FA();
      }
    };
    const handleCloseDialog = () => {
      setOpenOTPDialog(false);
    };
    const handleIsSessionLocked = () => {
      onIsSessionLocked();
    };

    // PopOver
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const openPopper = Boolean(anchorEl);
    const id = open ? COMMONCONSTANTS.SIMPLEPOPOVER : undefined;
    const handleClickOutsideHover = () => {
      setAnchorEl(null);
    };

    useEffect(() => {
      if (anchorEl) {
        document.addEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      } else {
        document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      }
      return () => {
        document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      };
    }, [anchorEl]);

    const handleClickOutside = (event: MouseEvent) => {
      if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
        setAnchorEl(null);
      }
    };

    const handleCustomOtpChange = (otp) => {
      updateAuthAppState({ inputToken: otp });
    };
    return (
      <>
        <Typography component="h1" variant="h1" className="paper-title">
          {headerText} Sign up with 2FA App
          <div className="popper-wrapper">
            <Tooltip title={POPOVERMESSAGES.HelpTooltip} arrow placement="top">
              <IconButton
                id="SignUpHelpOutIcon"
                tabIndex={-1}
                aria-describedby={id}
                onClick={handleClick}
                className="help-icon-color"
              >
                {<HelpOutline className="help-outline-icon" />}
              </IconButton>
            </Tooltip>
            {/* Popover */}
            <Popper
              id={id}
              open={openPopper}
              anchorEl={anchorEl}
              ref={popperRef}
              className="signup-popper"
            >
              <Paper sx={{ boxShadow: 3 }} className="signup-paper">
                {/* Arrow */}
                <div className="arrow" />
                <div dangerouslySetInnerHTML={{ __html: POPOVERMESSAGES.SIGNUP_WITH_2FA }} />
                <div className="popover-btn">
                  {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                  <button onClick={handleClickOutsideHover} autoFocus className="button">
                    {COMMONCONSTANTS.GOTIT}
                  </button>
                </div>
              </Paper>
            </Popper>
          </div>
        </Typography>
        {loading && (
          <Box className="circularprogress-box">
            <CircularProgress />
          </Box>
        )}
        <Box textAlign="center">
          {authAppState.isVisible && (
            <div>
              <Button
                id="btnAutheticatorApp"
                fullWidth
                variant="contained"
                color={"primary"}
                onClick={() => (uiCondition ? handleGenerate2FA() : handleClickOpen())}
                sx={{ mt: "5px" }}
              >
                Set up an authenticator app
              </Button>
            </div>
          )}
          {authAppState.isSetUp && (
            <>
              <h6 className="otp-required-text">Enter the verification code</h6>
              <Box textAlign="center"></Box>
              <p></p>
            </>
          )}
        </Box>
        <div>
          <OtpHandler
            ref={otpHandlerRef}
            email={email}
            phone={phone}
            userName={userName}
            otpSendTo={otpSendTo}
            onOtpVerified={onCheckOtpVerify}
            onClose={handleCloseDialog}
            open={openOTPDialog}
            onIsSessionLocked={handleIsSessionLocked}
          />
        </div>
        <Dialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={open}
          maxWidth="sm"
          fullWidth
        >
          {/* Dialog Title */}
          <DialogTitle
            id="customized-dialog-title"
            sx={{ fontWeight: "bold", textAlign: "center" }}
            className="dialogtitle-font-size"
          >
            {step === 3 ? "Verification Successful" : "Set up Multi-Factor Authentication"}
          </DialogTitle>

          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Dialog Content */}
          <DialogContent dividers sx={{ p: 3, textAlign: "center" }}>
            {step === 1 ? (
              // 🔹 STEP 1: QR CODE SETUP
              <>
                <Typography variant="body2" color="primary" gutterBottom>
                  STEP 1
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Download an authenticator app such as Microsoft Authenticator or Google
                  Authenticator.
                </Typography>

                <Typography variant="body2" color="primary" sx={{ mt: 2 }} gutterBottom>
                  STEP 2
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Scan the QR Code below with the authenticator app.
                </Typography>

                {/* QR Code Section */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Paper elevation={1} sx={{ p: "10px" }} variant="outlined">
                    {authAppState.qrCode && (
                      <img src={`${authAppState.qrCode}`} alt="" className="sign-in-qrcode" />
                    )}
                  </Paper>
                </Box>

                <Divider sx={{ my: 2, fontSize: "14px" }}>OR enter the code manually</Divider>

                {/* Manual Code Input */}
                <TextField
                  variant="outlined"
                  fullWidth
                  value={authAppState.secret}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={handleCopy}>
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Snackbar
                  open={showCopiedMessage}
                  autoHideDuration={2000}
                  onClose={() => setShowCopiedMessage(false)}
                  message="Copied!"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Alert severity="success">{USERSIGNUPMESSAGES.MANUAL_CODE_COPIED}</Alert>
                </Snackbar>
              </>
            ) : step === 2 ? (
              // 🔹 STEP 2: OTP INPUT
              <>
                <Typography variant="body2" color="primary" gutterBottom>
                  STEP 3
                </Typography>
                {isSessionLocked && (
                  <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-alert">
                    {USERSIGNINMESSAGES.SESSION_LOCKED_MESSAGE.replace(
                      "{Session_value}",
                      localStorage.getItem(USERINFO.SESSIONVALUE),
                    )}
                  </Alert>
                )}
                <Alert
                  severity="warning"
                  sx={{ marginBottom: "10px" }}
                  className="alert-success-popup"
                >
                  {USERSIGNUPMESSAGES.AUTH_APP_IS_SETUP}
                </Alert>
                <Typography variant="body2" color="textSecondary">
                  Enter the 6-digit OTP generated by the authenticator app.
                </Typography>
                <div className={isSessionLocked ? "disabled-div" : ""}>
                  {/* OTP Input Field */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                      mt: 3,
                    }}
                  >
                    <Box
                      display="flex"
                      gap={1}
                      sx={{
                        flexWrap: "wrap",
                        width: "100%",
                        maxWidth: {
                          xs: "100%",
                          sm: `${otpFieldLength * 63}px`,
                        },
                      }}
                    >
                      <OtpInput
                        length={otpFieldLength}
                        onChange={handleCustomOtpChange}
                        ref={otpRef}
                      />
                    </Box>
                  </Box>
                </div>
                <p className="required-msg">{tokenErrorMessage}</p>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <TaskAltSharpIcon sx={{ fontSize: 64, color: "success.light" }} />
                <Typography variant="h1" sx={{ mt: 2 }}>
                  Verified!
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                  You can now use MFA to verify your identity when logging in.
                </Typography>
              </Box>
            )}
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            {step === 2 && (
              <div className={isSessionLocked ? "disabled-div" : ""}>
                <Button
                  variant="contained"
                  color="light"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setStep(1)}
                  sx={{
                    mr: 1,
                    px: 3,
                    borderRadius: 2,
                  }}
                >
                  Back
                </Button>
              </div>
            )}

            {step === 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  updateAuthAppState({ inputToken: "" });
                  setTokenErrorMessage("");
                  setStep(2);
                }}
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 3, borderRadius: 2 }}
              >
                Continue
              </Button>
            ) : step === 2 ? (
              <>
                <div className={isSessionLocked ? "disabled-div" : ""}>
                  <Button
                    variant="contained"
                    onClick={verify2FA}
                    endIcon={
                      loadingVerify ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                    sx={{ px: 3, borderRadius: 2 }}
                    disabled={loadingVerify}
                  >
                    {loadingVerify ? "Verifying..." : "Verify"}
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="contained" onClick={handleClose} sx={{ px: 3, borderRadius: 2 }}>
                Done
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </>
    );
  },
);
export default SignUpWithTwoFactorAuth;
